import React from 'react';
import { useIntl } from 'react-intl';

type FormatterMeta = { userTyping?: boolean; input?: string };

type NumberInputFormat = {
  formatter: (value: number | string | undefined, meta?: FormatterMeta) => string;
  parser: (value: string | undefined) => string;
};

// No configured precision, or a precision of 0, both mean "this field is integer-only".
const isIntegerOnly = (precision?: number): boolean => precision === undefined || precision === 0;

const useNumberInputFormat = (precision?: number): NumberInputFormat => {
  const intl = useIntl();

  const formatter = React.useCallback(
    (value: number | string | undefined, meta?: FormatterMeta): string => {
      // While the user is typing, return the raw input as-is
      if (meta?.userTyping) {
        return meta.input ?? String(value ?? '');
      }

      // Explicit null/undefined/empty guard — Number(null) = Number('') = 0,
      // which would incorrectly display "0" when the field should be empty.
      if (value == null || value === '') {
        return '';
      }

      const num = Number(value);
      // eslint-disable-next-line no-restricted-globals
      if (isNaN(num)) {
        return '';
      }

      // No thousands grouping: keeping the display free of any group separator means a re-opened
      // field never contains a stray character that could be confused with a decimal point.
      return intl.formatNumber(num, {
        useGrouping: false,
        ...(precision !== undefined && {
          minimumFractionDigits: precision,
          maximumFractionDigits: precision,
        }),
      });
    },
    [intl, precision]
  );

  const parser = React.useCallback(
    (value: string | undefined): string => {
      if (!value) {
        return '';
      }

      // Accept both ',' and '.' as the decimal separator, regardless of locale. Safe to do
      // unconditionally since the display never contains a thousands separator to confuse it with.
      const cleaned = value.replace(',', '.');

      // Non-numeric input (e.g. "abc") → return '' so antd treats it as empty rather than clamping to min
      // eslint-disable-next-line no-restricted-globals
      if (cleaned !== '-' && cleaned !== '.' && isNaN(Number(cleaned))) {
        return '';
      }

      if (isIntegerOnly(precision)) {
        const truncated = Math.trunc(Number(cleaned));
        // eslint-disable-next-line no-restricted-globals
        return isNaN(truncated) ? cleaned : String(truncated);
      }

      return cleaned;
    },
    [precision]
  );

  return { formatter, parser };
};

export default useNumberInputFormat;
